/**
 * Class which handles loading and filtering new content into the page.
 * 
 * @exports
 * @class ContentLoader
 */
// @ts-ignore
import * as objectFitImages from 'object-fit-images';

import { serialize } from '../../helpers/serialize';
import { get, getAll, getParameterByName, removeURLParameter } from '../../helpers/utilities';

import { Parser } from './parser';

export class ContentLoader {
    private root: Element;
    private parser = new Parser();
    private options: ContentLoaderOptions;
    private isLoadingRequest = false;
    private noResults: Element;


    /**
     * Initialises a new instance of ContentLoader
     * 
     * @param root The root element for this block
     */
    constructor(root: Element, options: ContentLoaderOptions) {
        this.root         = root;
        this.options      = options;
        this.noResults    = get('#js-content-loader__no-results') as Element;

        this.bindEvents();
    }


    /**
     * Binds the events for this class
     * 
     * @private
     * @returns void
     */
    private bindEvents(): void {

        // Add more content on click
        this.root.addEventListener('click', (event) => {
            const target = (event.target as HTMLElement).classList.contains('js-content-loader__add') ? (event.target as HTMLElement) : (event.target as HTMLElement).closest('.js-content-loader__add');
            if(target) {
                let url = (event.target as HTMLAnchorElement).href;
                url = this.updateQuery(url);
                this.sendRequest(url);
                event.preventDefault();
            }
        });

        // Replace the existing content on click
        this.root.addEventListener('click', (event) => {
            const target = (event.target as HTMLElement).classList.contains('js-content-loader__replace') ? (event.target as HTMLElement) : (event.target as HTMLElement).closest('.js-content-loader__replace');
            if(target) {
                let url = (target as HTMLAnchorElement).href;
                url = this.updateQuery(url);
                this.sendRequest(url, true);
                event.preventDefault();
            }
        });

        // Send request on form submissions
        const forms = getAll('.js-content-loader__form') as HTMLFormElement[];
        if(forms) {
            forms.forEach(form => {
                form.addEventListener('submit', (event) => {
                    const url = this.updateQuery(form.action, form);
                    this.sendRequest(url, true);
                    event.preventDefault();
                });
            });
        }

        // Submit on change
        const changeInput = getAll('.js-content-loader__submit-on-change') as HTMLSelectElement[];
        if(changeInput) {
            changeInput.forEach(input => {
                input.addEventListener('change', () => {
                    const form = input.closest('form') as HTMLFormElement;
                    const url = this.updateQuery(form.action, form);
                    this.sendRequest(url, true);
                });
            });
        }

        // Submit on input
        // @ts-ignore
        let timeout: Timeout;
        const textInputs = getAll('.js-content-loader__submit-on-input') as HTMLInputElement[];
        if(textInputs) {
            textInputs.forEach(input => {
                input.addEventListener('keyup', () => {
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                        const form = input.closest('form') as HTMLFormElement;
                        const url = this.updateQuery(form.action, form);
                        this.sendRequest(url, true);
                    }, 300);
                });
            });
        }
    }


    /**
     * Sends the Ajax Request.
     * 
     * @param url The URL to request
     */
    public sendRequest(url: string, replaceContent = false): void {
        if(this.isLoadingRequest) return;
        this.options.toggleUI(true);
        this.isLoadingRequest = true;

        // Create our request and open the URL
        const request = new XMLHttpRequest();
        request.open('GET', url, true);

        // Identify as an Ajax Request
        request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        // Set our load event
        request.addEventListener('load', () => {
            if (request.status >= 200 && request.status < 400) {
                this.options.toggleUI(false);
                this.isLoadingRequest = false;
                
                // Pass the data to the renderer and then pass the response to the parser.
                const response    = JSON.parse(request.responseText) as ContentLoaderJSONResponse;
                this.handleResponse(response, replaceContent);

                // Update the URL
                if(history.pushState) {
                    const historyURL = removeURLParameter('ajax', url);
                    history.pushState('', '', historyURL);
                }
            } else {
                this.options.toggleUI(false);
                this.isLoadingRequest = false;
                alert( 'There was an issue loading the next page of the content. Please try again later.');
            }
        });
        
        // Set our error handler
        request.addEventListener('error', () => {
            this.options.toggleUI(false);
            this.isLoadingRequest = false;
            alert( 'There was an issue loading the next page of the content. Please try again later.');
        });

        // Send the request
        request.send();
    }


    /**
     * Updates the URL with additional parameters, such as JSON identification, filters etc
     * 
     * @param url The URL to modify
     */
    private updateQuery(url: string, form?: HTMLFormElement): string {

        // Add our JSON identifier
        const hasExistingQuery = new RegExp(/\?+/g).test(url);
        if(!getParameterByName('ajax')) {
            const parts = url.split('#');
            if(parts.length > 1) {
                url = `${parts[0]}${(hasExistingQuery ? '&ajax' : '?ajax')}#${parts[1]}`;
            } else {
                url += hasExistingQuery ? '&ajax' : '?ajax'
            }
        }

        // Serialise our form values
        if(form) {

            // @ts-ignore
            const formData  = serialize(form) as string;
            const formParts = formData.split('&');
            let queryString      = '';
            formParts.forEach(query => {
                const parts = query.split('=');
                if(parts[1]) {
                    queryString += queryString !== '' ? '&' : '';
                    queryString += `${parts[0]}=${parts[1]}`;
                }
            });
            url = `${url}&${queryString}`;
        }
        
        return url;
    }


    /**
     * Handles the response from the server.
     * 
     * @param newElementStrings The HTML element strings.
     * @param nextPageURL The next page URL in the series of pagination
     */
    private handleResponse(response: ContentLoaderJSONResponse, replaceContent: boolean): void {
        this.toggleNoResults((response.items as ArrayLike<unknown>).length > 0);
        
        // Get the new elements and render them
        const newElementStrings = this.options.renderer(response);
        const newElements       = this.parser.parse(newElementStrings);

        if(replaceContent) {

            this.parser.clear(this.options.itemSelector, this.root, () => {
                this.parser.insert(newElements, this.options.insertOptions);

                // Execute our callback
                if(this.options.events && this.options.events.afterInsert) {
                    objectFitImages();
                    this.options.events.afterInsert(newElements);
                }
            })
        } else {
            this.parser.insert(newElements, this.options.insertOptions);

            // Execute our callback
            if(this.options.events && this.options.events.afterInsert) {
                objectFitImages();
                this.options.events.afterInsert(newElements);
            }
        }

        // Update the load more button
        this.toggleLoadMore(response.next_page);
    }


    /**
     * Toggle the Load more buttons.
     * 
     * @param nextPageURL The URL for the next page
     */
    private toggleLoadMore(nextPageURL: string): void {
        const addLinks = getAll('.js-content-loader__add', this.root) as Element[];

        if(nextPageURL) {

            // Remove the ajax query
            const url = removeURLParameter('ajax', nextPageURL);
            
            addLinks.forEach(link => {
                link.classList.remove('u-hidden');
                (link as HTMLAnchorElement).href = url
            });
        } else {
            
            // Update the link
            addLinks.forEach(link => {
                link.classList.add('u-hidden');
            });
        }
    }


    /**
     * Toggles the no results message
     * 
     * @private
     * @returns void
     */
    private toggleNoResults(hasItems: boolean): void {
        if(!this.noResults) return;

        if(hasItems) {
            this.noResults.classList.add('u-hidden');
        } else {
            this.noResults.classList.remove('u-hidden');
        }
    }
}
